import React, { useRef, useEffect, useState, useMemo } from 'react';
import { useAutocomplete } from '../hooks/useGoogleMaps';
import styles from './AddressAutocomplete.module.css';

const parseAddressComponents = (place) => {
  if (!place.address_components) return {};

  const getComponent = (type) => {
    const component = place.address_components.find(c => c.types.includes(type));
    return component ? component.long_name : '';
  };

  const getShortComponent = (type) => {
    const component = place.address_components.find(c => c.types.includes(type));
    return component ? component.short_name : '';
  };

  return {
    formatted_address: place.formatted_address || '',
    street_number: getComponent('street_number'),
    route: getComponent('route'),
    street_address: [getComponent('street_number'), getComponent('route')].filter(Boolean).join(' '),
    city:
      getComponent('locality') ||
      getComponent('postal_town') ||
      getComponent('sublocality_level_1') ||
      getComponent('sublocality') ||
      getComponent('neighborhood') ||
      getComponent('administrative_area_level_3') ||
      getComponent('administrative_area_level_2'),
    state: getShortComponent('administrative_area_level_1'),
    state_long: getComponent('administrative_area_level_1'),
    postal_code: getComponent('postal_code'),
    country: getShortComponent('country'),
    country_long: getComponent('country'),
    place_id: place.place_id,
    geometry: place.geometry
  };
};

const AddressAutocomplete = ({ 
  id,
  value, 
  onChange, 
  onPlaceChanged,
  placeholder = 'Enter address', 
  className,
  disabled = false,
  // Section token keeps browser autofill from filling other address blocks on the same page
  autoComplete = 'off'
}) => {
  const inputRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const onPlaceChangedRef = useRef(onPlaceChanged);
  const [localValue, setLocalValue] = useState(value || '');

  onChangeRef.current = onChange;
  onPlaceChangedRef.current = onPlaceChanged;

  const options = useMemo(() => ({
    types: ['address'],
    componentRestrictions: { country: 'us' },
    fields: ['address_components', 'formatted_address', 'geometry', 'place_id']
  }), []);
  
  const autocomplete = useAutocomplete(inputRef, options);

  // Set up place change listener once per Autocomplete instance
  useEffect(() => {
    if (!autocomplete) return;

    const listener = autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      
      if (place.formatted_address) {
        setLocalValue(place.formatted_address);
        onChangeRef.current?.(place.formatted_address);
        
        if (onPlaceChangedRef.current) {
          const addressData = parseAddressComponents(place);
          onPlaceChangedRef.current(addressData, place);
        }
      }
    });

    return () => {
      window.google?.maps?.event?.removeListener?.(listener);
    };
  }, [autocomplete]);

  // Update local value when prop changes
  useEffect(() => {
    setLocalValue(value || '');
  }, [value]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    onChangeRef.current?.(newValue);
  };

  return (
    <div className={`${styles.container} ${className || ''}`}>
      <input
        id={id}
        name={id}
        ref={inputRef}
        type="text"
        value={localValue}
        onChange={handleInputChange}
        placeholder={placeholder}
        className={styles.input}
        disabled={disabled}
        autoComplete={autoComplete}
      />
    </div>
  );
};

export default AddressAutocomplete;
